/**
 * String utilities — cleaning, formatting, initials extraction.
 *
 * Canonical implementations consolidated from:
 * - packages/ssm-web5/src/utils/string.jsx
 * - packages/hoi-poi-ui/src/utils/string.jsx
 * - apps/ssm-web4/src/app/utils/strings/index.js
 * - apps/ssm-admin/src/utils/string.jsx
 *
 * NOTE: React-dependent functions (breakLines) are intentionally excluded —
 * they stay in the packages/apps that need React.
 */

/**
 * Remove diacritical marks (accents) from a string using Unicode NFD
 * normalization. Also trims whitespace.
 *
 * Exported under both names for backward compatibility:
 * - `removeAccents` (hoi-poi-ui / web4 name)
 * - `cleanString` (ssm-web5 / admin name)
 *
 * Returns an empty string for falsy values to preserve legacy web4 behavior.
 *
 * @param {string} text
 * @returns {string}
 */
export const removeAccents = (text) => {
    if (!text) return '';

    return text
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
};

/** Alias — some consumers know this as `cleanString`. */
export const cleanString = removeAccents;

const MARKDOWN_BLOCKQUOTE_REGEX = /^\s{0,3}>\s?/gm;
const MARKDOWN_BOLD_REGEX = /\*\*([^*]+)\*\*/g;
const MARKDOWN_CODE_BLOCK_REGEX = /```(?:[\w-]+)?\n?([\s\S]*?)```/g;
const MARKDOWN_ESCAPE_REGEX = /\\([\\`*_{}\[\]()#+\-.!~>])/g;
const MARKDOWN_HEADING_REGEX = /^\s{0,3}#{1,6}\s+/gm;
const MARKDOWN_HORIZONTAL_RULE_REGEX = /^\s{0,3}(?:-{3,}|\*{3,}|_{3,})\s*$/gm;
const MARKDOWN_INLINE_CODE_REGEX = /`([^`]+)`/g;
const MARKDOWN_ITALIC_REGEX = /(^|[^\w])\*([^*\n]+)\*(?=[^\w]|$)/gm;
const MARKDOWN_LINK_REGEX = /!?\[([^\]]*)\]\(([^)]+)\)/g;
const MARKDOWN_LIST_ITEM_REGEX = /^(\s*)(?:[-*+]\s+|\d+\.\s+)/gm;
const MARKDOWN_STRIKETHROUGH_REGEX = /~~([^~]+)~~/g;
const EMOJI_REGEX =
    /(?:\p{Regional_Indicator}{2}|[\p{Emoji_Presentation}\p{Extended_Pictographic}]|\p{Emoji}\uFE0F)/gu;
const EMOJI_MODIFIER_REGEX = /[\u{1F3FB}-\u{1F3FF}]/gu;
const HTML_TAG_REGEX = /<[^>]+>/g;
const ZERO_WIDTH_JOINER_REGEX = /\u200D/gu;

/**
 * Sanitize a field name: strip accents, replace spaces/hyphens with `_`,
 * remove characters not matching the pattern, collapse consecutive `_`, and
 * optionally lower-case the result.
 *
 * @param {string} fieldName
 * @param {string} [pattern] — regex char-class pattern (default: `[^a-zA-Z0-9_]`)
 * @param {boolean} [forceLowerCase=true]
 * @returns {string}
 */
export function normalizeFieldName(fieldName, pattern, forceLowerCase = true) {
    const patt = new RegExp(pattern || '[^a-zA-Z0-9_]');
    let val = '';

    let name = fieldName.trim().replace(/\s|-/g, '_');
    name = removeAccents(name);

    name.split('').forEach((letter) => {
        if (!patt.test(letter)) {
            val += letter;
        }
    });

    // collapse consecutive underscores
    val = val.replace(/_+/g, '_');

    if (forceLowerCase) val = val.toLowerCase();
    return val;
}

/**
 * Capitalize the first character of a string.
 *
 * @param {string} item
 * @returns {string}
 */
export const capitalize = (item) => item.charAt(0).toUpperCase() + item.slice(1);

/**
 * Extract the first and last initials from a full name string.
 *
 * @param {string} name
 * @returns {string} Up to two uppercase letters (e.g. "JD")
 */
export const getFirstTwoInitials = (name) => {
    if (!name) return '';
    let initials = name.match(/\b\w/g) || [];
    const firstInitial = initials.shift();
    const lastInitial = initials.pop();
    initials = [firstInitial, lastInitial];
    initials = initials.join('').toUpperCase().substring(0, 2);
    return initials;
};

/**
 * Replace whole-word occurrences of `search` inside `obj` with `replace`,
 * preserving surrounding punctuation and whitespace.
 *
 * @param {string} obj — the text to search within
 * @param {string} search — the word to find
 * @param {string} replace — the replacement word
 * @returns {string}
 */
export const replaceWords = (obj, search, replace) => {
    const regExp = new RegExp(`(^|[^a-zA-ZΆΈ-ώἀ-ῼ\\n])(${search})(?![a-zA-ZΆΈ-ώἀ-ῼ])`, 'gm');
    const retStr = obj.replace(regExp, (match) => {
        if (match.length === 0 || replace.length === 0) {
            return replace;
        }

        let rpl = replace;
        const regex = new RegExp("(\\s|[!@#$%\\^&*)(+=._-]|'|')");

        const firstCh = match[0];
        const firstRplCh = replace[0];
        if (regex.test(firstCh) === true && firstCh !== firstRplCh) {
            rpl = `${firstCh}${rpl}`;
        }

        const lastCh = match[match.length - 1];
        const lastRplCh = replace[replace.length - 1];
        if (regex.test(lastCh) === true && lastCh !== lastRplCh) {
            rpl = `${rpl}${lastCh}`;
        }
        return rpl;
    });
    return retStr;
};

/**
 * Remove HTML tags from a string.
 *
 * @param {string} text
 * @returns {string}
 */
export const removeTags = (text) => {
    if (typeof text !== 'string') return text ?? '';
    return text.replace(HTML_TAG_REGEX, '');
};

/**
 * Remove common markdown syntax markers while preserving readable text.
 *
 * @param {string} text
 * @returns {string}
 */
export const removeMarkdown = (text) => {
    if (typeof text !== 'string' || !text) return text ?? '';

    return text
        .replace(MARKDOWN_CODE_BLOCK_REGEX, '$1')
        .replace(MARKDOWN_LINK_REGEX, '$1')
        .replace(MARKDOWN_INLINE_CODE_REGEX, '$1')
        .replace(MARKDOWN_BOLD_REGEX, '$1')
        .replace(MARKDOWN_STRIKETHROUGH_REGEX, '$1')
        .replace(MARKDOWN_ITALIC_REGEX, '$1$2')
        .replace(MARKDOWN_HEADING_REGEX, '')
        .replace(MARKDOWN_HORIZONTAL_RULE_REGEX, '')
        .replace(MARKDOWN_BLOCKQUOTE_REGEX, '')
        .replace(MARKDOWN_LIST_ITEM_REGEX, '$1')
        .replace(MARKDOWN_ESCAPE_REGEX, '$1');
};

/**
 * Remove emoji and emoji-joiner code points from a string.
 *
 * @param {string} text
 * @returns {string}
 */
export const removeEmoji = (text) => {
    if (typeof text !== 'string' || !text) return text ?? '';

    return text
        .replace(EMOJI_REGEX, '')
        .replace(EMOJI_MODIFIER_REGEX, '')
        .replace(ZERO_WIDTH_JOINER_REGEX, '');
};

/**
 * Return the second capture group from a regex match.
 *
 * @param {string} text
 * @param {RegExp} regex
 * @returns {string}
 */
export const getRegexMatch = (text, regex) => {
    const matchArr = text.match(regex);
    if (!matchArr) return '';
    return matchArr?.[2] || '';
};

/**
 * Execute a regex and normalize the result to a `{ match, info }` object.
 *
 * @param {string} text
 * @param {RegExp} regex
 * @returns {{ match?: string, info?: string }}
 */
export const getRegexExec = (text, regex) => {
    const matchArr = regex.exec(text) || [];

    if (!matchArr.length) return {};

    const match = matchArr?.[0] || '';
    const info = matchArr?.[2] || '';

    return { match, info };
};

/**
 * Return the suffix of `nextText` that differs from `previousText`.
 *
 * @param {string} previousText
 * @param {string} nextText
 * @returns {string}
 */
export const getDifference = (previousText, nextText) => {
    if (!previousText) return nextText;

    const arr1 = previousText.toLowerCase().split('');
    const arr2 = nextText.toLowerCase().split('');

    const index = arr2.findIndex((char, i) => char !== arr1[i]);

    return nextText.slice(index);
};

/**
 * Regex to test whether a string is a URL (starting with http(s):// or www.).
 * @type {RegExp}
 */
export const URL_REGEX = /^\b(((https?:\/\/)|(www\.))(\S+))$/;
